import {QRPayloadType} from '@Libraries/qr-code/qr-code.types';
import type {QRGeneratorAction, QRGeneratorState, QRSharedContactDetails} from '@Components/qr-code-generator/qr-code-generator.types';
import {QRGeneratorActionKind} from '@Components/qr-code-generator/qr-code-generator.types';
import {assertNever} from '@Utils/assert-never.util';

export const qrGeneratorReducer = (state: QRGeneratorState, action: QRGeneratorAction): QRGeneratorState => {
  switch (action.kind) {
    case QRGeneratorActionKind.SET_ACTIVE_TYPE:
      return seedActiveTypeFromSharedDetails({...state, activeType: action.activeType});
    case QRGeneratorActionKind.PATCH_URL:
      return {
        ...state,
        urlInput: {...state.urlInput, ...action.patch},
        sharedContactDetails: rememberSharedDetails(state.sharedContactDetails, {website: action.patch.link}),
      };
    case QRGeneratorActionKind.PATCH_SOCIAL:
      return {...state, socialInput: {...state.socialInput, ...action.patch}};
    case QRGeneratorActionKind.PATCH_TEXT:
      return {...state, textInput: {...state.textInput, ...action.patch}};
    case QRGeneratorActionKind.PATCH_EMAIL:
      return {
        ...state,
        emailInput: {...state.emailInput, ...action.patch},
        sharedContactDetails: rememberSharedDetails(state.sharedContactDetails, {emailAddress: action.patch.emailAddress}),
      };
    case QRGeneratorActionKind.PATCH_PHONE:
      return {
        ...state,
        phoneInput: {...state.phoneInput, ...action.patch},
        sharedContactDetails: rememberSharedDetails(state.sharedContactDetails, {phoneNumber: action.patch.phoneNumber}),
      };
    case QRGeneratorActionKind.PATCH_SMS:
      return {
        ...state,
        smsInput: {...state.smsInput, ...action.patch},
        sharedContactDetails: rememberSharedDetails(state.sharedContactDetails, {phoneNumber: action.patch.phoneNumber}),
      };
    case QRGeneratorActionKind.PATCH_WIFI:
      return {...state, wifiInput: {...state.wifiInput, ...action.patch}};
    case QRGeneratorActionKind.PATCH_VCARD:
      return {
        ...state,
        vCardInput: {...state.vCardInput, ...action.patch},
        sharedContactDetails: rememberSharedDetails(state.sharedContactDetails, {
          phoneNumber: action.patch.mobileNumber,
          emailAddress: action.patch.emailAddress,
          website: action.patch.website,
        }),
      };
    case QRGeneratorActionKind.PATCH_CALENDAR_EVENT:
      return {...state, calendarEventInput: {...state.calendarEventInput, ...action.patch}};
    case QRGeneratorActionKind.PATCH_PAYMENT_LINK:
      return {...state, paymentLinkInput: {...state.paymentLinkInput, ...action.patch}};
    case QRGeneratorActionKind.PATCH_APP_STORE:
      return {...state, appStoreInput: {...state.appStoreInput, ...action.patch}};
    case QRGeneratorActionKind.PATCH_MULTIMEDIA:
      return {...state, multimediaInput: {...state.multimediaInput, ...action.patch}};
    case QRGeneratorActionKind.PATCH_STYLE:
      return {...state, style: {...state.style, ...action.patch}};
    case QRGeneratorActionKind.SET_LOGO:
      return {...state, logo: action.logo};
    default:
      return assertNever(action);
  }
};

const rememberSharedDetails = (remembered: QRSharedContactDetails, patch: Partial<QRSharedContactDetails>): QRSharedContactDetails => {
  return {
    phoneNumber: patch.phoneNumber ?? remembered.phoneNumber,
    emailAddress: patch.emailAddress ?? remembered.emailAddress,
    website: patch.website ?? remembered.website,
  };
};

const seedActiveTypeFromSharedDetails = (state: QRGeneratorState): QRGeneratorState => {
  const {phoneNumber, emailAddress, website} = state.sharedContactDetails;

  switch (state.activeType) {
    case QRPayloadType.URL:
      return {...state, urlInput: {...state.urlInput, link: fillIfBlank(state.urlInput.link, website)}};
    case QRPayloadType.EMAIL:
      return {...state, emailInput: {...state.emailInput, emailAddress: fillIfBlank(state.emailInput.emailAddress, emailAddress)}};
    case QRPayloadType.PHONE:
      return {...state, phoneInput: {...state.phoneInput, phoneNumber: fillIfBlank(state.phoneInput.phoneNumber, phoneNumber)}};
    case QRPayloadType.SMS:
      return {...state, smsInput: {...state.smsInput, phoneNumber: fillIfBlank(state.smsInput.phoneNumber, phoneNumber)}};
    case QRPayloadType.VCARD:
      return {
        ...state,
        vCardInput: {
          ...state.vCardInput,
          mobileNumber: fillIfBlank(state.vCardInput.mobileNumber, phoneNumber),
          emailAddress: fillIfBlank(state.vCardInput.emailAddress, emailAddress),
          website: fillIfBlank(state.vCardInput.website, website),
        },
      };
    case QRPayloadType.SOCIAL:
    case QRPayloadType.TEXT:
    case QRPayloadType.WIFI:
    case QRPayloadType.CALENDAR_EVENT:
    case QRPayloadType.PAYMENT_LINK:
    case QRPayloadType.APP_STORE:
    case QRPayloadType.MULTIMEDIA:
      return state;
    default:
      return assertNever(state.activeType);
  }
};

const fillIfBlank = (current: string, remembered: string): string => {
  return current === '' ? remembered : current;
};
